import classNames from "classnames";
import { greaterThan, subtract } from "dinero.js";
import React from "react";
import SVG from "react-inlinesvg";
import { TransitionGroup } from "react-transition-group";
import { TGetProductUpsell } from "tsi-common-react/src/apps/checkout/BasketLine";
import { CSSTransitionWithRef } from "tsi-common-react/src/components/CssTransition/index";
import {
    IBasket,
    IBasketLine,
    IProduct,
} from "tsi-common-react/src/models/catalogue";
import { IProductAPIURL } from "tsi-common-react/src/models/nominals";
import { getDinero, toUnit } from "tsi-common-react/src/utils/money";

import iconCircle from "../../../assets/svg/circle.svg";
import { BasketLineSuggestion } from "../BasketLineSuggestion";

import styles from "./index.module.scss";

const listSuggestedProducts = (
    basket: IBasket,
    line: IBasketLine,
): readonly IProduct[] => {
    if (!line.bundles) {
        return [];
    }

    // Create a mapping of products IDs in the basket to the price of the item
    const pids = basket.lines.map((l) => {
        return l.product.url;
    });

    // Flatten suggested products from bundles
    let products = line.bundles
        .map((bundle) => {
            return bundle.suggested_products;
        })
        .reduce((memo, suggestions) => {
            return memo.concat(suggestions);
        }, []);

    // Figure out the minimum cost to include as a suggestion.
    const minSuggestionPrice = products.reduce((memo, product) => {
        const price = getDinero(product.price.cosmetic_excl_tax || "0.00");
        if (pids.indexOf(product.url) !== -1 && greaterThan(price, memo)) {
            return price;
        }
        return memo;
    }, getDinero(0));

    // Filter out products already in the basket
    products = products.filter((product) => {
        const isAlreadyInCart = pids.indexOf(product.url) !== -1;
        const isAboveSugestedPrice = greaterThan(
            getDinero(product.price.cosmetic_excl_tax || "0.00"),
            minSuggestionPrice,
        );
        if (!isAlreadyInCart && isAboveSugestedPrice) {
            pids.push(product.url);
            return true;
        }
        return false;
    });

    // Sort from most expensive to least expensive
    const sortedProducts = [...products].sort(
        (productA: IProduct, productB: IProduct) => {
            const priceA = getDinero(
                productA.price.cosmetic_excl_tax || "0.00",
            );
            const priceB = getDinero(
                productB.price.cosmetic_excl_tax || "0.00",
            );
            return toUnit(subtract(priceB, priceA));
        },
    );

    return sortedProducts;
};

const BasketLineSuggestions = (props: {
    basket: IBasket;
    line: IBasketLine;
    addBasketLine: (
        url: IProductAPIURL,
        qty: number,
    ) => Promise<IBasket | null>;
}): React.ReactNode => {
    const [suggestionIndex, setSuggestionIndex] = React.useState(0);

    const products = listSuggestedProducts(props.basket, props.line);
    const product = products[suggestionIndex];

    if (!product) {
        return null;
    }

    const prevSuggestion = () => {
        const i = Math.max(0, suggestionIndex - 1);
        setSuggestionIndex(i);
    };

    const nextSuggestion = () => {
        const i = Math.min(products.length - 1, suggestionIndex + 1);
        setSuggestionIndex(i);
    };

    const circles = products.map((_, index) => {
        const circleClasses = classNames({
            "feel-scale-circles__item": true,
            "feel-scale-circles__item--filled": suggestionIndex !== index,
        });

        return <SVG key={index} className={circleClasses} src={iconCircle} />;
    });

    const hasPrev = suggestionIndex > 0;
    const hasNext = suggestionIndex < products.length - 1;

    const suggestionControls = products.length > 1 && (
        <div className={styles.basketLineDetailsSuggestionControls}>
            <button onClick={prevSuggestion} disabled={!hasPrev}>
                &lt;
            </button>
            <span className="basket-line__details__suggestion-controls__circles">
                {circles}
            </span>
            <button onClick={nextSuggestion} disabled={!hasNext}>
                &gt;
            </button>
        </div>
    );

    const basketSuggestion = (
        <BasketLineSuggestion
            line={props.line}
            product={product}
            basketTotal={getDinero(props.basket.total_incl_tax)}
            addBasketLine={props.addBasketLine}
        />
    );

    return (
        <>
            <TransitionGroup>
                <CSSTransitionWithRef
                    key={`${product.url}`}
                    classNames="transition--fade"
                    timeout={{ exit: 500, enter: 500 }}
                >
                    {basketSuggestion}
                </CSSTransitionWithRef>
            </TransitionGroup>
            {suggestionControls}
        </>
    );
};

export const getLineProductUpsell: TGetProductUpsell = (
    basket,
    line,
    addBasketLine,
) => {
    const content = (
        <BasketLineSuggestions
            basket={basket}
            line={line}
            addBasketLine={addBasketLine}
        />
    );
    return {
        placement: "in-details",
        hideBottomBorder: false,
        content: content,
    };
};
